import { BsCashStack } from "react-icons/bs";
import ErrorMessage from "../ErrorMessage";
import toast from "react-hot-toast";
import axios from "axios";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Transactions } from "@/types/globalTypes";
import { Skeleton } from "../ui/skeleton";
import { AppConfig } from "@/utils/AppConfig";
import PaginationControls from "../PaginationControls";
import { useTranslation } from "react-i18next";
import { formatMoney } from "@/utils/helpers";

export default function StoreCashHistory() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [transactionsHistory, setTransactionsHistory] = useState<
    Transactions[]
  >([] as Transactions[]);
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  // Pagination
  const [pageNum, setPageNum] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const url = `${BASE_URL}/api/dashboard/settings/wallet/transactions?type=cash&page=${currentPage}&length=${pageNum}`;

  const handleSetPageNumber = (page: number) => {
    setPageNum(page);
    setCurrentPage(1);
  };


  useEffect(() => {
    if (!token) return;

    setIsLoading(true);
    setError(null);
    const fetchTransactions = async () => {
      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: `${token}`,
          },
        });

        if (!response.data.success) {
          const errMessage =
            response.data.error ||
            (t(
              "storeCashHistory.unexpectedError"
            ) as string);
          setError(errMessage);
          toast.error(errMessage);
          return;
        }

        setTransactionsHistory(response.data.transactions);
        setTotalItems(response.data.pagination.totalItems);
        setTotalPages(response.data.pagination.totalPages);
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data.error ||
            (t(
              "storeCashHistory.fetchFailed"
            ) as string);
          setError(message);
          toast.error(message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, [token, t, url]);

  if (isLoading)
    return (
      <div className="">
        <Skeleton className="h-[200px] w-full dark:bg-slate-600 dark:bg-opacity-35" />
      </div>
    );
  if (error) return <ErrorMessage msg={error} />;

  return (
    <section className="w-full">
      <div className="w-full whitespace-nowrap rounded-md border bg-[#FCFCFC] p-5">
        <h1 className="flex items-center gap-2 font-semibold text-slate-500">
          {" "}
          <BsCashStack />
          {t("storeCashHistory.title")}
          <span> ({totalItems})</span>{" "}
        </h1>
        <div className="mt-5">
          <div className="thin-scrollbar relative overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-500 rtl:text-right">
              <thead className="bg-[#F2F5F7] uppercase text-gray-400">
                <tr>
                  <th scope="col" className="px-2 py-3">
                    {t(
                      "storeCashHistory.transactionNumber"
                    )}
                  </th>
                
                  <th scope="col" className="px-2 py-3">
                    {t(
                      "storeCashHistory.transactionType"
                    )}
                  </th>
                
                  <th scope="col" className="px-2 py-3">
                    {t(
                      "storeCashHistory.transactionAmount"
                    )}
                  </th>
                  <th scope="col" className="px-2 py-3">
                    {t(
                      "storeCashHistory.transactionDate"
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactionsHistory.length > 0 ? (
                  transactionsHistory.map((transaction, index) => (
                    <tr
                      key={transaction.id || index}
                      className="border-b bg-[#FFFFFF]"
                    >
                      <td className="px-2 py-4">{transaction.id || "-"}</td>
                      {/* <td className="px-2 py-4">{transaction.wallet || "-"}</td> */}
                      <td className="px-2 py-4">{transaction.type || "-"}</td>
                      {/* <td className="px-2 py-4">
                        {transaction.totalItems || "-"}
                      </td> */}
                      <td dir="rtl" className="px-2 py-4">
                        {transaction.amount ? `${formatMoney(transaction.amount)} ` : "-"}
                        {transaction.amount && AppConfig.currency.icon()}
                      </td>
                      <td className="px-2 py-4">
                        {transaction.time
                          ? new Date(
                            transaction.time * 1000,
                          ).toLocaleDateString("en-GB") // dd/mm/yyyy format
                          : "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-2 py-4 text-center text-gray-500"
                    >
                      {t(
                        "storeCashHistory.noTransactions"
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <PaginationControls
        pageNum={pageNum}
        setPageNum={handleSetPageNumber}
        totalItems={totalItems}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </section>
  );
}
